package com.royceecoleman.bill.pages

import com.royceecoleman.bill.shared.aichat.BILL_SUPPORTED_MODEL_PROVIDERS
import com.royceecoleman.bill.shared.aichat.ManagedAiModelDto
import com.royceecoleman.bill.shared.aichat.ManagedAiModelType
import com.royceecoleman.bill.shared.aichat.ManagedAiModelUpdateRequest
import com.royceecoleman.bill.shared.aichat.SPRING_AI_FRAMEWORK
import com.royceecoleman.bill.shared.aichat.canBeRemoved
import com.royceecoleman.bill.shared.aichat.validateManagedModelRequest

/** Editable form state for the AI model management card. Temperature is kept as text while typing. */
data class ManagedModelEditorState(
    val id: String = "",
    val name: String = "",
    val provider: String = BILL_SUPPORTED_MODEL_PROVIDERS.first(),
    val deploymentName: String = "",
    val temperature: String = "1.0",
    val isExisting: Boolean = false,
)

fun emptyManagedModelEditorState(): ManagedModelEditorState = ManagedModelEditorState()

fun ManagedAiModelDto.toEditorState(): ManagedModelEditorState =
    ManagedModelEditorState(
        id = id,
        name = name,
        provider = provider.ifBlank { BILL_SUPPORTED_MODEL_PROVIDERS.first() },
        deploymentName = deploymentName,
        temperature = temperature.toString(),
        isExisting = true,
    )

fun ManagedModelEditorState.toRequest(): ManagedAiModelUpdateRequest =
    ManagedAiModelUpdateRequest(
        id = id.trim(),
        type = ManagedAiModelType.CHAT,
        name = name.trim(),
        provider = provider.trim().uppercase(),
        deploymentName = deploymentName.trim(),
        temperature = temperature.trim().toDoubleOrNull() ?: Double.NaN,
        supportedFrameworks = listOf(SPRING_AI_FRAMEWORK),
    )

/** Returns the first validation problem, or null when the editor state can be submitted. */
fun ManagedModelEditorState.validationError(): String? {
    if (temperature.trim().toDoubleOrNull() == null) {
        return "Temperature must be a number."
    }
    return validateManagedModelRequest(toRequest())
}

/** Returns the reason a model cannot be deleted, or null when deletion is allowed. */
fun deleteBlockedReason(model: ManagedAiModelDto): String? =
    if (model.canBeRemoved()) null else "Select a new default model before deleting '${model.id}'."

fun managedModelSummary(model: ManagedAiModelDto): String =
    buildString {
        append(model.provider.ifBlank { "UNKNOWN" })
        append(" · ")
        append(model.deploymentName.ifBlank { "no deployment" })
        append(" · temp ")
        append(model.temperature)
        if (model.isDefault) append(" · default")
        if (model.deprecated) append(" · deprecated")
    }
